import { faReceipt } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AccountBreadcrumb } from '../components/AccountBreadcrumb';
import { EmptyState } from '../components/EmptyState';
import {
	DEFAULT_HISTORY_RANGE,
	HistoryFilters,
	resolveHistoryRange,
} from '../components/HistoryFilterBar';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { Paginator } from '../components/Paginator';
import { HistoryListSkeleton } from '../components/skeletons';
import { TransactionRow } from '../components/TransactionRow';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { appScrollToTop } from '../lib/appScroll';
import * as portalTransactionService from '../services/portalTransactionService';
import { useLoginModalStore } from '../stores/loginModalStore';

const PAGE_SIZE = 20;

/**
 * Transaction history (PD-14080) — payments, captures, voids, refunds across
 * the customer's assigned clients, modeled on the invoices page and scoped
 * server-side exactly like the CRM transactions list is for the client.
 */
export function TransactionsPage(): JSX.Element {
	const { t } = useTranslation();
	const { token, isAuthenticated, availableClients } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const [searchParams, setSearchParams] = useSearchParams();

	const currentPage = parseInt(searchParams.get('page') ?? '1', 10);
	const clientIdParam = searchParams.get('clientId') ?? '';
	// Relative range key ('' = the default, last 3 months) resolved to
	// concrete ISO bounds for the API (PD-14674).
	const rangeParam = searchParams.get('range') ?? '';
	const { dateFrom, dateTo } = resolveHistoryRange(rangeParam);

	const { data, isLoading } = useQuery({
		queryKey: ['portal-transactions', currentPage, clientIdParam, rangeParam],
		queryFn: () =>
			portalTransactionService.getTransactions(token ?? '', {
				page: currentPage,
				limit: PAGE_SIZE,
				clientId:
					clientIdParam.length > 0 ? parseInt(clientIdParam, 10) : undefined,
				dateFrom,
				dateTo,
			}),
		enabled: token != null,
		staleTime: 30_000,
	});

	const transactions = data?.data ?? [];
	const pagination = data?.pagination ?? null;

	const updateParams = useCallback(
		(updates: Record<string, string | null>) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				for (const [key, value] of Object.entries(updates)) {
					if (value == null || value.length === 0) {
						next.delete(key);
					} else {
						next.set(key, value);
					}
				}
				return next;
			});
		},
		[setSearchParams],
	);

	const handlePageChange = useCallback(
		(page: number) => {
			updateParams({ page: page > 1 ? String(page) : null });
			appScrollToTop('smooth');
		},
		[updateParams],
	);

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	return (
		<div className="px-4 py-4 max-w-4xl mx-auto">
			<MobilePageHeader title="Transactions" backPath="/account" />
			<AccountBreadcrumb current="Transactions" />
			<h1 className="hidden md:block text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
				{t('Transactions')}
			</h1>

			{/* One filter tray (date range / location) with Apply-Cancel
			    (PD-14701). No keyword search on transactions. */}
			<div className="mb-4">
				<HistoryFilters
					fields={[
						{ kind: 'range', key: 'range', label: 'Date Range' },
						...(availableClients.length > 1
							? [
									{
										kind: 'location' as const,
										key: 'clientId' as const,
										label: 'Location',
										clients: availableClients,
									},
								]
							: []),
					]}
					values={{ range: rangeParam, clientId: clientIdParam }}
					onApply={(next) => {
						const range = next.range;
						const clientId = next.clientId;
						updateParams({
							range:
								range.length > 0 && range !== DEFAULT_HISTORY_RANGE
									? range
									: null,
							clientId: clientId.length > 0 ? clientId : null,
							page: null,
						});
					}}
				/>
			</div>

			{isLoading ? (
				/* Skeleton cards shaped like the list (PD-14692) */
				<HistoryListSkeleton />
			) : transactions.length === 0 ? (
				/* Empty state (PD-14699) — filtered-empty variants first. */
				clientIdParam.length > 0 ? (
					<EmptyState
						icon={faReceipt}
						title="No transactions for this location"
						hint="Try removing the location filter to see more results."
						actionLabel="Clear filter"
						onAction={() => {
							updateParams({ clientId: null, page: null });
						}}
					/>
				) : rangeParam !== 'all' ? (
					<EmptyState
						icon={faReceipt}
						title="No transactions in this date range"
						hint="Older activity may fall outside the selected range."
						actionLabel="View all time"
						onAction={() => {
							updateParams({ range: 'all', page: null });
						}}
					/>
				) : (
					<EmptyState
						icon={faReceipt}
						title="No transactions yet"
						hint="Payments and credits will appear here once processed."
					/>
				)
			) : (
				<div className="flex flex-col gap-2">
					{transactions.map((txn) => (
						<TransactionRow
							key={txn.id}
							txn={txn}
							showClient={availableClients.length > 1}
						/>
					))}
				</div>
			)}

			{pagination != null && pagination.totalPages > 1 && (
				<div className="mt-4">
					<Paginator
						currentPage={pagination.page}
						totalPages={pagination.totalPages}
						onPageChange={handlePageChange}
					/>
				</div>
			)}
		</div>
	);
}
